import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useAuth } from '../../../context/AuthContext/AuthContext';
import PostWithdrawalRequestService from '../service/postWithdrawalRequest';
import {
  AvailableTransactionInstrumentDTO,
  BankAccount,
  CryptoAccount,
  PaymentMethodType,
  TransactionProvider,
  TransactionMethodInstrument,
  WithdrawalInitRequestDTO,
  WithdrawalInitResponseDTO,
  WithdrawalMethodDetails,
} from '../service/models';
import { WithdrawMethodOption } from '../contents/PaymentMethodStep/useAvailableWithdrawInstruments';

import AmountStep from '../contents/AmountStep/AmountStep';
import PaymentMethodStep from '../contents/PaymentMethodStep/PaymentMethodStep';
import BankAccountStep from '../contents/BankAccountStep/BankAccountStep';
import RfxWithdrawStep from '../contents/RfxWithdrawStep/RfxWithdrawStep';
import BridgerPayWidget from '../../../components/PaymentWidget/BridgerPayWidget';

export interface StepConfig {
  render: () => React.ReactNode;
  validate: () => boolean;
  isValid: () => boolean;
}

interface UseWithdrawStepsProps {
  withdrawableBalance: number;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onComplete: () => void;
  onBridgerPayClose: () => void;
  setOpenAddBankModal: (open: boolean) => void;
  setOpenAddCryptoModal: (open: boolean) => void;
  onCloseModal?: () => void;
}

/**
 * Resolves the instrument and provider for a bank withdrawal based on:
 * 1. Which instruments the API returned as available (from the method option)
 * 2. Which the selected bank account is eligible for (sepaEligible / swiftEligible)
 * If both are available, SEPA is preferred.
 */
const resolveBankInstrument = (
  bankInstruments: AvailableTransactionInstrumentDTO[],
  account: BankAccount,
): { instrument: TransactionMethodInstrument; provider: TransactionProvider } => {
  const sepaAvailable = bankInstruments.find((i) => i.instrument === 'BANK_TRANSFER_SEPA');
  const swiftAvailable = bankInstruments.find((i) => i.instrument === 'BANK_TRANSFER_SWIFT');

  // Prefer SEPA when both the API offers it and the account is eligible
  if (sepaAvailable && account.sepaEligible) {
    return { instrument: 'BANK_TRANSFER_SEPA', provider: sepaAvailable.provider };
  }
  if (swiftAvailable && account.swiftEligible) {
    return { instrument: 'BANK_TRANSFER_SWIFT', provider: swiftAvailable.provider };
  }

  // Fallback: shouldn't happen if validation is correct, but pick whatever is available
  const fallback = sepaAvailable ?? swiftAvailable!;
  return { instrument: fallback.instrument, provider: fallback.provider };
};

interface BuildWithdrawalParams {
  userId: string;
  amount: string;
  paymentMethod: PaymentMethodType;
  selectedBankAccount: BankAccount | null;
  selectedCryptoAccount: CryptoAccount | null;
  selectedRfxAccountId: number | null;
  selectedRfxAccountNumber: string | null;
  selectedMethodOption: WithdrawMethodOption | null;
}

const buildWithdrawalRequest = ({
  userId,
  amount,
  paymentMethod,
  selectedBankAccount,
  selectedCryptoAccount,
  selectedRfxAccountId,
  selectedRfxAccountNumber,
  selectedMethodOption,
}: BuildWithdrawalParams): WithdrawalInitRequestDTO => {
  let method: TransactionProvider;
  let instrument: TransactionMethodInstrument;
  let methodDetails: WithdrawalMethodDetails;

  switch (paymentMethod) {
    case 'BANK': {
      const resolved = resolveBankInstrument(
        selectedMethodOption!.instruments,
        selectedBankAccount!,
      );
      method = resolved.provider;
      instrument = resolved.instrument;
      methodDetails = { type: 'BANK', bankAccountId: selectedBankAccount!.bankAccountId };
      break;
    }
    case 'CARD': {
      method = 'BRIDGERPAY';
      instrument = 'CREDIT_CARD';
      methodDetails = { type: 'CARD' };
      break;
    }
    case 'CRYPTO': {
      const cryptoInstrument = selectedMethodOption!.instruments[0];
      method = cryptoInstrument.provider;
      instrument = cryptoInstrument.instrument;
      methodDetails = { type: 'CRYPTO', cryptoAccountId: selectedCryptoAccount!.id };
      break;
    }
    case 'TRADING_ACCOUNT': {
      const tradingInstrument = selectedMethodOption!.instruments[0];
      method = tradingInstrument.provider;
      instrument = tradingInstrument.instrument;
      methodDetails = { type: 'TRADING_ACCOUNT', tradingAccountId: selectedRfxAccountId!, tradingAccountLogin: Number(selectedRfxAccountNumber) };
      break;
    }
    default:
      throw new Error(`Unsupported payment method: ${paymentMethod}`);
  }

  const request: WithdrawalInitRequestDTO = {
    currency: 'EUR',
    method,
    instrument,
    userId,
    methodDetails,
    amount,
  };

  if (instrument === 'TRADING_PAYOUT' && selectedRfxAccountNumber) {
    request.targetAccount = selectedRfxAccountNumber;
  }

  return request;
};

export const useWithdrawSteps = ({
  withdrawableBalance,
  onSuccess,
  onError,
  onComplete,
  onBridgerPayClose,
  setOpenAddBankModal,
  setOpenAddCryptoModal,
  onCloseModal
}: UseWithdrawStepsProps) => {
  const { t } = useTranslation('area');
  const { tokens, user, profile } = useAuth();

  const [amount, setAmount] = useState<string>('');
  const [amountError, setAmountError] = useState<string>('');

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType | null>(null);
  const [selectedMethodOption, setSelectedMethodOption] = useState<WithdrawMethodOption | null>(null);
  const [methodError, setMethodError] = useState<string>('');

  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [cryptoAccounts, setCryptoAccounts] = useState<CryptoAccount[]>([]);

  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount | null>(null);
  const [selectedCryptoAccount, setSelectedCryptoAccount] = useState<CryptoAccount | null>(null);
  const [selectedTradingAccountId, setSelectedTradingAccountId] = useState<number | null>(null);
  const [selectedTradingAccountNumber, setSelectedTradingAccountNumber] = useState<string | null>(null);
  const [accountError, setAccountError] = useState<string>('');

  // BridgerPay state
  const [bridgerPaySession, setBridgerPaySession] = useState<WithdrawalInitResponseDTO | null>(null);
  const [bridgerPayLoading, setBridgerPayLoading] = useState(false);
  const resetForm = () => {
    setAmount('');
    setAmountError('');
    setPaymentMethod(null);
    setSelectedMethodOption(null);
    setMethodError('');
    setAccountError('');
    setSelectedBankAccount(null);
    setSelectedCryptoAccount(null);
    setSelectedTradingAccountId(null);
    setSelectedTradingAccountNumber(null);
    setBridgerPaySession(null);
    setBridgerPayLoading(false);
  };

  const isBankAccountSelectable = (account: BankAccount): boolean => {
    if (!selectedMethodOption) return true;
    const instruments = selectedMethodOption.instruments;
    const sepaAvailable = instruments.some((i) => i.instrument === 'BANK_TRANSFER_SEPA');
    const swiftAvailable = instruments.some((i) => i.instrument === 'BANK_TRANSFER_SWIFT');
    return (sepaAvailable && account.sepaEligible) || (swiftAvailable && account.swiftEligible);
  };
  const fetchBankAccounts = async () => {
    if (tokens) {
      try {
        const data = await PostWithdrawalRequestService.getUserBankAccounts(tokens.accessToken);
        setBankAccounts(data);
        if (data.length > 0 && !selectedBankAccount) {
          const firstSelectable = data.find((a) => isBankAccountSelectable(a));
          if (firstSelectable) setSelectedBankAccount(firstSelectable);
        }
      } catch (e) { console.error(e); }
    }
  };

  const fetchCryptoAccounts = async () => {
    if (tokens) {
      try {
        const data = await PostWithdrawalRequestService.getUserCryptoAccounts(tokens.accessToken);
        setCryptoAccounts(data);
        if (data.length > 0 && !selectedCryptoAccount) setSelectedCryptoAccount(data[0]);
      } catch (e) { console.error(e); }
    }
  };

  useEffect(() => {
    if (tokens) {
      fetchBankAccounts();
      fetchCryptoAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens]);

  const refreshBanks = () => fetchBankAccounts();
  const refreshCrypto = () => fetchCryptoAccounts();


  const isAmountValid = (): boolean => {
    const num = parseFloat(amount);
    return !!amount && !isNaN(num) && num > 0 && num <= withdrawableBalance;
  };

  const isMethodValid = (): boolean => {
    if (!paymentMethod) return false;
    if (paymentMethod === 'BANK' && parseFloat(amount) > 8400) return false;
    return true;
  };

  const isAccountValid = (): boolean => {
    if (paymentMethod === 'BANK') return !!selectedBankAccount;
    if (paymentMethod === 'CRYPTO') return !!selectedCryptoAccount;
    if (paymentMethod === 'TRADING_ACCOUNT') return !!selectedTradingAccountId;
    if (paymentMethod === 'CARD') return true; // Card details entered via BridgerPay widget
    return false;
  };

  const validateAmountStep = (): boolean => {
    const num = parseFloat(amount);

    if (!amount || isNaN(num) || num <= 0) {
      setAmountError(t('MODAL_WITHDRAW.CONTENT.STEP_THREE.ERROR_PLEASE_ENTER_A_VALID_AMOUNT'));
      return false;
    }

    if (num > withdrawableBalance) {
      setAmountError(t('MODAL_WITHDRAW.CONTENT.STEP_THREE.ERROR_THE_AMOUNT_CANNOT_EXCEED_THE_WALLET_BALANCE'));
      return false;
    }

    setAmountError('');
    return true;
  };

  const validateMethodStep = (): boolean => {
    if (!paymentMethod) {
        setMethodError(t('MODAL_WITHDRAW.CONTENT.STEP_ONE.ERROR_YOU_HAVE_TO_SELECT_A_PAIEMENT_METHOD'));
        return false;
    }

    const numAmount = parseFloat(amount);
    if (paymentMethod === 'BANK' && numAmount > 8400) {
        onError(t('MODAL_WITHDRAW.CONTENT.STEP_THREE.ERROR_THE_AMOUNT_CANNOT_EXCEED_8400_FOR_CREDIT_CARD_PAYMENTS'));
        return false;
    }

    setMethodError('');
    return true;
  };

  const validateAccountStep = (): boolean => {
    if (paymentMethod === 'BANK' && !selectedBankAccount) {
       setAccountError(t('Veuillez sélectionner ou ajouter un compte bancaire'));
       return false;
    }
    if (paymentMethod === 'CRYPTO' && !selectedCryptoAccount) {
        setAccountError(t('Veuillez sélectionner ou ajouter un compte crypto'));
        return false;
    }
    if (paymentMethod === 'TRADING_ACCOUNT' && !selectedTradingAccountId) {
        setAccountError(t('Veuillez sélectionner un compte de trading'));
        return false;
    }
    // CARD payment method doesn't need validation here - handled by BridgerPay widget
    setAccountError('');
    return true;
  };

  // Initiate card withdrawal and get BridgerPay session
  const initiateCardWithdrawal = useCallback(async (): Promise<boolean> => {
    if (!tokens || !user || bridgerPaySession) return true; // Already initiated

    setBridgerPayLoading(true);
    try {
      const dto = buildWithdrawalRequest({
        userId: user.userId,
        amount,
        paymentMethod: 'CARD',
        selectedBankAccount: null,
        selectedCryptoAccount: null,
        selectedRfxAccountId: null,
        selectedRfxAccountNumber: null,
        selectedMethodOption,
      });

      const response = await PostWithdrawalRequestService.submitWithdrawalRequest(
        tokens.accessToken,
        dto,
        () => {}, // Don't call success yet - wait for widget completion
        onError,
      );

      // Response is wrapped in CustomApiResponse, access .data for actual DTO
      const sessionData = (response as any)?.data || response;
      if (sessionData?.cashierToken && sessionData?.cashierKey && sessionData?.externalPaymentId) {
        setBridgerPaySession(sessionData);
        return true;
      } else {
        onError(t('MODAL_WITHDRAW.TOAST.CARD_SESSION_ERROR'));
        return false;
      }
    } catch (error) {
      console.error('Failed to initiate card withdrawal', error);
      return false;
    } finally {
      setBridgerPayLoading(false);
    }
  }, [tokens, user, amount, bridgerPaySession, selectedMethodOption, onError, t]);

  const submitWithdrawal = async (): Promise<WithdrawalInitResponseDTO | null> => {
    try {
      if (tokens && user) {
        const dto = buildWithdrawalRequest({
          userId: user.userId,
          amount,
          paymentMethod: paymentMethod as PaymentMethodType,
          selectedBankAccount,
          selectedCryptoAccount,
          selectedRfxAccountId: selectedTradingAccountId,
          selectedRfxAccountNumber: selectedTradingAccountNumber,
          selectedMethodOption,
        });

        const response = await PostWithdrawalRequestService.submitWithdrawalRequest(
          tokens.accessToken,
          dto,
          (message: string) => {
            onSuccess(message);
          },
          (message: string) => {
            onError(message);
          },
        );
        return response;
      }
      return null;
    } catch (error) {
      console.error('Erreur soumission retrait', error);
      onError(t('MODAL_WITHDRAW.TOAST.GENERAL_ERROR'));
      return null;
    }
  };

  const submitBridgerPayCardToken = async (
    transactionExternalId: string,
    creditCardToken: string,
  ): Promise<boolean> => {
    try {
      if (tokens) {
        await PostWithdrawalRequestService.submitBridgerPayCardToken(tokens.accessToken, {
          transactionExternalId,
          creditCardToken,
        });
        onSuccess(t('MODAL_WITHDRAW.TOAST.WITHDRAWAL_REQUEST_SUCCESS'));
        onComplete();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erreur soumission token BridgerPay', error);
      onError(t('MODAL_WITHDRAW.TOAST.CARD_SUBMISSION_ERROR'));
      setBridgerPaySession(null);
      onBridgerPayClose();
      return false;
    }
  };

  const getSteps = (): StepConfig[] => {
    const baseSteps: StepConfig[] = [
        {
          render: () => (
            <AmountStep
              value={amount}
              onChange={(v) => {
                setAmount(v);
                const num = parseFloat(v);
                if (!isNaN(num) && num > withdrawableBalance) {
                  setAmountError(t('MODAL_WITHDRAW.CONTENT.AMOUNT_STEP.ERROR_EXCEEDS_BALANCE', { balance: withdrawableBalance }));
                } else {
                  setAmountError('');
                }
              }}
              error={amountError}
              walletBalance={withdrawableBalance}
            />
          ),
          validate: validateAmountStep,
          isValid: isAmountValid,
        },
        {
          render: () => (
            <PaymentMethodStep
                selectedMethod={paymentMethod}
                onSelect={(method, option) => {
                    setPaymentMethod(method);
                    setSelectedMethodOption(option);
                    setMethodError('');
                }}
                error={methodError}
            />
          ),
          validate: validateMethodStep,
          isValid: isMethodValid,
        },
    ];

    if (paymentMethod === 'BANK') {
        baseSteps.push({
            render: () => (
                <BankAccountStep
                    type="BANK"
                    items={bankAccounts}
                    selectedId={selectedBankAccount?.bankAccountId}
                    onSelect={(item) => setSelectedBankAccount(item as BankAccount)}
                    onAdd={() => setOpenAddBankModal(true)}
                    error={accountError}
                    availableInstruments={selectedMethodOption?.instruments}
                />
            ),
            validate: validateAccountStep,
            isValid: isAccountValid,
        });
    } else if (paymentMethod === 'CRYPTO') {
        baseSteps.push({
            render: () => (
                <BankAccountStep
                    type="CRYPTO"
                    items={cryptoAccounts}
                    selectedId={selectedCryptoAccount?.id}
                    onSelect={(item) => setSelectedCryptoAccount(item as CryptoAccount)}
                    onAdd={() => setOpenAddCryptoModal(true)}
                    error={accountError}
                />
            ),
            validate: validateAccountStep,
            isValid: isAccountValid,
        });
    } else if (paymentMethod === 'TRADING_ACCOUNT') {
        baseSteps.push({
            render: () => (
                <RfxWithdrawStep
                    selectedAccountId={selectedTradingAccountId}
                    setSelectedAccountId={setSelectedTradingAccountId}
                    setSelectedAccountNumber={setSelectedTradingAccountNumber}
                    onCloseModal={onCloseModal}
                />
            ),
            validate: validateAccountStep,
            isValid: isAccountValid,
        });
    } else if (paymentMethod === 'CARD') {
        // BridgerPay widget step for card payments
        baseSteps.push({
            render: () => {
                // Show loading state while fetching session
                if (bridgerPayLoading) {
                    return (
                        <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                            <CircularProgress size={48} />
                            <Typography sx={{ mt: 2, color: 'text.secondary' }}>
                                Loading payment form...
                            </Typography>
                        </Stack>
                    );
                }

                // Show widget when session is ready
                if (bridgerPaySession?.cashierKey && bridgerPaySession?.cashierToken) {
                    return (
                        <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', py: 2 }} spacing={2}>
                            <BridgerPayWidget
                                cashierKey={bridgerPaySession.cashierKey}
                                cashierToken={bridgerPaySession.cashierToken}
                                externalId={bridgerPaySession.externalPaymentId || ''}
                                onClose={() => {
                                    setBridgerPaySession(null);
                                    onBridgerPayClose();
                                }}
                                onTokenReceived={async (token: string, externalId: string) => {
                                    await submitBridgerPayCardToken(externalId, token);
                                }}
                            />
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setBridgerPaySession(null);
                                    onBridgerPayClose();
                                }}
                                sx={{ minWidth: 120 }}
                            >
                                {t('MODAL_WITHDRAW.BUTTONS.BACK', 'Back')}
                            </Button>
                        </Stack>
                    );
                }

                // Show error/waiting state if no session yet
                return (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                        <Typography sx={{ color: 'text.secondary' }}>
                            Initializing payment...
                        </Typography>
                    </Stack>
                );
            },
            validate: () => true,
            isValid: () => true, // Always valid, widget handles the flow
        });
    }

    return baseSteps;
  };

  return {
    steps: getSteps(),
    amount,
    paymentMethod,
    selectedBankAccount,
    selectedCryptoAccount,
    selectedTradingAccountId,
    submitWithdrawal,
    submitBridgerPayCardToken,
    refreshBanks,
    refreshCrypto,
    resetForm,
    // BridgerPay specific
    bridgerPaySession,
    bridgerPayLoading,
    initiateCardWithdrawal,
  };
};
